// Importaciones agrupadas por categorías
// React y Redux
import { useEffect, useRef, useState } from 'react';
import { useSelector } from "react-redux";

// DataTables y dependencias
import "datatables.net";
import "datatables.net-bs5";
import "datatables.net-buttons";
import "datatables.net-buttons-bs5";
import "datatables.net-buttons/js/buttons.html5";
import "datatables.net-searchpanes-bs5";
import "datatables.net-select-bs5";
import $ from "jquery";
import "jszip"; // Necesario para exportación Excel

// Configuración API
import { apiUrl, commonRequestData } from "../../../../api/api";

/**
 * Componente para generar reportes de agenda
 * Permite visualizar y exportar datos de agenda filtrados por fecha
 */
export const ReporteAgenda = () => {
    // Referencias para la tabla
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);

    // Datos del usuario autenticado
    const { rol_admin, id_admin } = useSelector((state) => state.auth);

    // Estados para el rango de fechas del reporte
    const [startDate, setStartDate] = useState(getLastWeekDate());
    const [endDate, setEndDate] = useState(getNextYearDate());

    /**
     * Devuelve la fecha de hace una semana en formato YYYY-MM-DD
     */
    function getLastWeekDate() {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date.toISOString().split('T')[0];
    }

    /**
     * Devuelve la fecha de un año adelante en formato YYYY-MM-DD
     */
    function getNextYearDate() {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        return date.toISOString().split('T')[0];
    }

    /**
     * Maneja los cambios en los campos de fecha
     */
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        if (name === "startDate") setStartDate(value);
        else if (name === "endDate") setEndDate(value);
    };

    /**
     * Recarga la tabla con los nuevos filtros de fecha
     */
    const updateTable = () => {
        if (dataTableRef.current) {
            // Destruir la tabla actual
            dataTableRef.current.destroy();
            dataTableRef.current = null;

            // Reinicializar la tabla para aplicar nuevos filtros y ordenamiento
            // Lo hacemos con un pequeño retraso para asegurar que la destrucción se complete
            setTimeout(() => {
                // Esta llamada forzará la re-ejecución del useEffect que contiene la inicialización
                setStartDate(prevDate => {
                    // Forzar re-renderizado sin cambiar el valor actual
                    return prevDate;
                });
            }, 100);
        }
    };

    /**
     * Genera columnas adicionales para el archivo Excel exportado
     */
    const customizeExcel = (xlsx) => {
        const sheet = xlsx.xl.worksheets['sheet1.xml'];
        const rows = $('row', sheet);

        // Formatear fechas al estilo día/mes/año
        rows.each(function (i) {
            if (i === 0) return; // Saltar la fila de encabezado

            // Celda de fecha (columna B)
            const fechaCell = $('c[r^="B"]', this);
            if (fechaCell.length > 0) {
                const fecha = fechaCell.text();
                if (fecha && fecha !== '') {
                    try {
                        const dateObj = new Date(fecha);
                        if (!isNaN(dateObj.getTime())) {
                            const dia = String(dateObj.getDate()).padStart(2, '0');
                            const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
                            const anio = dateObj.getFullYear();
                            const formattedDate = `${dia}/${mes}/${anio}`;

                            // Reemplazar con la fecha formateada
                            fechaCell.html(`<is><t>${formattedDate}</t></is>`);
                            fechaCell.attr('t', 'inlineStr');
                        }
                    } catch (e) {
                        console.error("Error al formatear fecha en Excel:", e);
                    }
                }
            }

            // Formatear las celdas de hora (columnas C y D)
            const horaInicioCell = $('c[r^="C"]', this);
            const horaFinCell = $('c[r^="D"]', this);

            // Formatear hora inicio
            if (horaInicioCell.length > 0) {
                const hora = horaInicioCell.text();
                if (hora && hora.includes(':')) {
                    horaInicioCell.html(`<is><t>${hora}</t></is>`);
                    horaInicioCell.attr('t', 'inlineStr');
                }
            }

            // Formatear hora fin
            if (horaFinCell.length > 0) {
                const hora = horaFinCell.text();
                if (hora && hora.includes(':')) {
                    horaFinCell.html(`<is><t>${hora}</t></is>`);
                    horaFinCell.attr('t', 'inlineStr');
                }
            }
        });

        // Lógica para agregar columnas de kilómetros
        const headers = $('c', rows.first());
        const lastHeader = $(headers[headers.length - 1]).attr('r');

        // Extraer la parte de letra de la referencia de celda
        const colLetter = lastHeader.replace(/\d+$/, '');

        // Calcular siguiente letra de columna (simplificado)
        const getNextColumnLetter = (letter) => {
            // Para columnas simples A-Z
            if (letter.length === 1) {
                return letter === 'Z' ? 'AA' : String.fromCharCode(letter.charCodeAt(0) + 1);
            }
            // Para columnas AA-ZZ
            const lastChar = letter.slice(-1);
            const prefix = letter.slice(0, -1);
            if (lastChar === 'Z') {
                return getNextColumnLetter(prefix) + 'A';
            }
            return prefix + String.fromCharCode(lastChar.charCodeAt(0) + 1);
        };

        const kmInicioCol = getNextColumnLetter(colLetter);
        const kmFinalCol = getNextColumnLetter(kmInicioCol);

        // Añadir encabezados para columnas de kilómetros
        $('row:first', sheet).append(
            `<c t="inlineStr" r="${kmInicioCol}1"><is><t>Kilómetro inicio</t></is></c>` +
            `<c t="inlineStr" r="${kmFinalCol}1"><is><t>Kilómetro final</t></is></c>`
        );

        // Añadir celdas vacías para todas las filas
        rows.not(':first').each(function (i) {
            const rowNum = i + 2; // La primera fila de datos es la 2
            $(this).append(
                `<c r="${kmInicioCol}${rowNum}" t="s"><v></v></c>` +
                `<c r="${kmFinalCol}${rowNum}" t="s"><v></v></c>`
            );
        });
    };

    /**
     * Formatea la fecha para mostrar día de la semana y fecha en español
     */
    const formatearFecha = (fecha) => {
        if (!fecha) return '';

        const date = new Date(fecha);
        const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

        return `${diasSemana[date.getDay()]} ${date.getDate()} de ${meses[date.getMonth()]}`;
    };

    useEffect(() => {
        // Configuración de columnas para la tabla
        const columns = [
            { data: 'name_admin', title: 'Vendedora', searchPanes: { show: true } },
            {
                data: 'fecha_inicio_agenda',
                title: 'Día',
                searchPanes: { show: true },
                render: function (data) {
                    if (!data) return '';
                    const fecha = new Date(data);
                    const dia = String(fecha.getDate()).padStart(2, '0');
                    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
                    const anio = fecha.getFullYear();
                    return `${dia}/${mes}/${anio}`;
                }
            },
            {
                data: 'hora_inicio_agenda',
                title: 'Hora inicio',
                render: function (data, type, row) {
                    if (!data) return '';
                    // Extraer horas y minutos directamente del string "HH:MM:SS"
                    const [horas, minutos] = data.split(':');
                    const horasNum = parseInt(horas);
                    const ampm = horasNum >= 12 ? 'PM' : 'AM';
                    const horas12 = horasNum % 12 || 12; // Convertir a formato 12h
                    return `${String(horas12).padStart(2, '0')}:${minutos} ${ampm}`;
                }
            },
            {
                data: 'hora_fin_agenda',
                title: 'Hora final',
                render: function (data, type, row) {
                    if (!data) return '';
                    // Extraer horas y minutos directamente del string "HH:MM:SS"
                    const [horas, minutos] = data.split(':');
                    const horasNum = parseInt(horas);
                    const ampm = horasNum >= 12 ? 'PM' : 'AM';
                    const horas12 = horasNum % 12 || 12; // Convertir a formato 12h
                    return `${String(horas12).padStart(2, '0')}:${minutos} ${ampm}`;
                }
            },
            { data: 'nombre_empresa', title: 'Empresa', searchPanes: { show: true } },
            { data: 'nombre_cliente', title: 'Cliente', searchPanes: { show: true } },
            { data: 'descripcion_agenda', title: 'Descripción' },
            { data: 'ubicacion_agenda', title: 'Ubicación', searchPanes: { show: true } },
            { data: 'tipo_agenda', title: 'Tipo', searchPanes: { show: true } },
            {
                data: 'estado_agenda',
                title: 'Estado',
                searchPanes: { show: true },
                render: function (data) {
                    // Agregar un span con clase para facilitar el ordenamiento
                    return `<span class="orden-estado-${data === 'pendiente' ? '1' : data === 'completado' ? '2' : '3'}">${data}</span>`;
                }
            },
        ];

        // Definir objeto de idioma español directamente
        const spanishLanguage = {
            "sProcessing": "Procesando...",
            "sLengthMenu": "Mostrar _MENU_ registros",
            "sZeroRecords": "No se encontraron resultados",
            "sEmptyTable": "Ningún dato disponible en esta tabla",
            "sInfo": "Mostrando registros del _START_ al _END_ de un total de _TOTAL_ registros",
            "sInfoEmpty": "Mostrando registros del 0 al 0 de un total de 0 registros",
            "sInfoFiltered": "(filtrado de un total de _MAX_ registros)",
            "sInfoPostFix": "",
            "sSearch": "Buscar:",
            "sUrl": "",
            "sInfoThousands": ",",
            "sLoadingRecords": "Cargando...",
            "oPaginate": {
                "sFirst": "Primero",
                "sLast": "Último",
                "sNext": "Siguiente",
                "sPrevious": "Anterior"
            },
            "oAria": {
                "sSortAscending": ": Activar para ordenar la columna de manera ascendente",
                "sSortDescending": ": Activar para ordenar la columna de manera descendente"
            },
            "buttons": {
                "copy": "Copiar",
                "colvis": "Visibilidad"
            },
            "searchPanes": {
                "title": {
                    "_": "Filtros seleccionados - %d",
                    "0": "Sin filtros seleccionados",
                    "1": "Un filtro seleccionado"
                },
                "clearMessage": "Limpiar Todo",
                "collapse": {
                    "0": "Filtros",
                    "_": "Filtros (%d)"
                }
            }
        };

        // Inicializar DataTable
        const dataTable = $(tableRef.current).DataTable({
            processing: true,
            serverSide: false,
            destroy: true, // Destruir tabla si ya existe

            // Especificar orden explícito por estado (columna 9)
            order: [[9, 'asc']],

            columnDefs: [
                {
                    targets: 9, // Columna de estado (índice 9)
                    type: 'html', // Importante: usar HTML para extraer el valor de ordenamiento del span
                    createdCell: function (cell, cellData) {
                        // Asignar clase para styling basado en estado
                        if (cellData === 'pendiente') $(cell).addClass('estado-pendiente');
                        if (cellData === 'completado') $(cell).addClass('estado-completado');
                        if (cellData === 'cancelado') $(cell).addClass('estado-cancelado');
                    }
                },
            ],

            // Configuración para pre-procesamiento de datos en la fuente
            ajax: {
                url: `${apiUrl}agenda/reporte`,
                type: 'POST',
                data: () => ({
                    ...commonRequestData,
                    startDate,
                    endDate,
                    rol_admin,
                    id_admin,
                }),
                dataSrc: function (response) {
                    let data = Array.isArray(response) ? response : (response.data || []);

                    // Definir función de ordenamiento estricta
                    data.sort((a, b) => {
                        // 1. Primero por estado (pendiente → completado → cancelado)
                        const estadoOrder = {
                            'pendiente': 1,
                            'completado': 2,
                            'cancelado': 3
                        };
                        const estadoA = estadoOrder[a.estado_agenda.toLowerCase()] || 4;
                        const estadoB = estadoOrder[b.estado_agenda.toLowerCase()] || 4;

                        if (estadoA !== estadoB) {
                            return estadoA - estadoB;
                        }

                        // 2. Luego por fecha
                        const fechaA = new Date(a.fecha_inicio_agenda);
                        const fechaB = new Date(b.fecha_inicio_agenda);

                        if (fechaA.getTime() !== fechaB.getTime()) {
                            return fechaA - fechaB;
                        }

                        // 3. Finalmente por hora
                        const [horasA] = a.hora_inicio_agenda.split(':');
                        const [horasB] = b.hora_inicio_agenda.split(':');
                        return parseInt(horasA) - parseInt(horasB);
                    });

                    return data;
                }
            },

            // Resto de la configuración...
            dom: 'BPfrtip',
            buttons: [
                {
                    extend: 'excel',
                    text: 'Exportar a Excel',
                    className: 'btn btn-success',
                    title: 'Reporte de Agenda',
                    exportOptions: { columns: ':visible' },
                    customize: customizeExcel
                }
            ],

            // Configuración de paneles de búsqueda
            searchPanes: {
                layout: 'columns-3',
                initCollapsed: true,
                cascadePanes: true,
                dtOpts: { select: { style: 'multi' } },
                viewTotal: true
            },

            columns,
            // Usar el objeto de idioma definido en lugar de cargarlo desde una URL
            language: spanishLanguage,
            responsive: true
        });

        // Guardar referencia para actualizaciones
        dataTableRef.current = dataTable;

        // Limpiar al desmontar
        return () => {
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                $(tableRef.current).DataTable().destroy();
                dataTableRef.current = null;
            }
        };
    }, []); // Solo se ejecuta una vez al montar el componente

    useEffect(() => {
        // Añadir estilos inline para colorear estados
        const styles = `
            .estado-pendiente { font-weight: bold; color: #3498db; }
            .estado-completado { font-weight: bold; color: #2ecc71; }
            .estado-cancelado { font-weight: bold; color: #e74c3c; }
        `;

        // Añadir estilos al documento
        const styleElement = document.createElement('style');
        styleElement.innerHTML = styles;
        document.head.appendChild(styleElement);

        // Limpiar al desmontar
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    return (
        <div className="container-fluid mt-4">
            <h2 className="mb-4">Reporte de Agenda</h2>

            {/* Filtros de fecha */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="form-group">
                        <label htmlFor="startDate">Fecha inicial:</label>
                        <input
                            type="date"
                            className="form-control"
                            id="startDate"
                            name="startDate"
                            value={startDate}
                            onChange={handleDateChange}
                        />
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="form-group">
                        <label htmlFor="endDate">Fecha final:</label>
                        <input
                            type="date"
                            className="form-control"
                            id="endDate"
                            name="endDate"
                            value={endDate}
                            onChange={handleDateChange}
                        />
                    </div>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                    <button
                        className="btn btn-primary"
                        onClick={updateTable}
                    >
                        Aplicar filtros
                    </button>
                </div>
            </div>

            {/* Tabla de reportes */}
            <table ref={tableRef} className="table table-striped table-bordered" width="100%">
                <thead>
                    <tr>
                        <th>Vendedora</th>
                        <th>Día</th>
                        <th>Hora inicio</th>
                        <th>Hora final</th>
                        <th>Empresa</th>
                        <th>Cliente</th>
                        <th>Descripción</th>
                        <th>Ubicación</th>
                        <th>Tipo</th>
                        <th>Estado</th>
                    </tr>
                </thead>
                <tbody>
                    {/* DataTables llenará esto automáticamente */}
                </tbody>
            </table>
        </div>
    );
};
